// ScrollIndicatorEcho — Scroll-Indikator, Design "Echo (final)" von Claude
// Design (Handoff 2026-05-27).
//
// 6×24px transparent-pulsende Pille, links 10px innerhalb der Stage. Top
// linear gemappt auf scroll-percent. Idle-Pulse nach 3s ohne Scroll-Event.
// Cover (scroll≈0): opacity 0. Colophon (~100%): 2s warten → fade-out, no
// re-show.
//
// Tweak vs. Echo-Spec (Bene 27.05.): width 4→6px, opacity active 0.20→0.35,
// idle-pulse 0.18-0.34 → 0.30-0.55, plus dezenter Dark-Halo (box-shadow)
// für Kontrast auf hellen Renderer-Inhalten. Spec war unsichtbar auf
// bunt-bildreicher Bühne — Aufdrehen behält "leise", kommt aber durch.
//
// Token-Quelle: handoff/design-reference/echo-final.html (Spec-Tabelle dort).
// Konsumiert via ViewerChrome → ScrollyContainer als chromeOverlay-Sibling.
//
// Position-Formel (A): top = PAD_TOP + (viewportH − PAD_TOP − PAD_BOT − H) × p
// Idle-Trigger (B):    inactivity ≥ 3000 ms, OFF bei scroll/touch/wheel/pointer
// Cover (C):           opacity 0 wenn p < 0.001
// Colophon (D):        p ≥ 0.985 → 2s Timer → opacity 0, kein Re-Show
//                      "Kein Re-Show" interpretieren als "solange am Colophon".
//                      Scrollt User zurück in die Story (p < threshold), darf
//                      Echo wieder erscheinen. Sonst würde ein einziger End-
//                      Besuch (z.B. via Resume-Restore auf scrollPercent=1)
//                      Echo permanent killen — UX-buggy.

import { useEffect, useRef, useState } from 'react';

interface Props {
  /** Scroll-Container dessen scrollTop/scrollHeight gelesen werden. */
  scrollContainerRef: React.RefObject<HTMLElement | null>;
  scrollSource?: 'container' | 'window';
}

const PAD_TOP = 60;
const PAD_BOTTOM = 24;
const ECHO_HEIGHT = 24;
const IDLE_DELAY_MS = 3000;
const COVER_THRESHOLD = 0.001;
const COLOPHON_THRESHOLD = 0.985;
const COLOPHON_FADE_DELAY_MS = 2000;

type Phase = 'cover' | 'active' | 'idle' | 'gone';

export function ScrollIndicatorEcho({
  scrollContainerRef,
  scrollSource = 'container',
}: Props) {
  const echoRef = useRef<HTMLDivElement | null>(null);
  const phaseRef = useRef<Phase>('cover');
  const lastActivityRef = useRef<number>(Date.now());
  const [phase, setPhaseState] = useState<Phase>('cover');

  // Mirror state → ref so rAF can read latest without re-subscribing.
  phaseRef.current = phase;

  useEffect(() => {
    const echo = echoRef.current;
    if (!echo) return;
    const el = scrollContainerRef.current;
    if (scrollSource === 'container' && !el) return;

    let raf = 0;
    let colophonTimer = 0;

    const setPhase = (next: Phase) => {
      if (phaseRef.current === next) return;
      phaseRef.current = next;
      setPhaseState(next);
    };

    const tick = () => {
      raf = requestAnimationFrame(tick);
      const viewportH =
        scrollSource === 'window'
          ? window.innerHeight || document.documentElement.clientHeight
          : (el?.clientHeight ?? 0);
      const scrollTop =
        scrollSource === 'window'
          ? window.scrollY ||
            document.documentElement.scrollTop ||
            document.body.scrollTop ||
            0
          : (el?.scrollTop ?? 0);
      const scrollHeight =
        scrollSource === 'window'
          ? Math.max(
              document.documentElement.scrollHeight,
              document.body.scrollHeight,
            )
          : (el?.scrollHeight ?? 0);
      const max = scrollHeight - viewportH;
      const percent = max > 0 ? Math.max(0, Math.min(1, scrollTop / max)) : 0;

      // Viewport-Höhe live aus dem aktiven Scroll-Root — ändert sich z.B.
      // wenn iOS-Safari die URL-Bar einklappt.
      const trackH = Math.max(
        0,
        viewportH - PAD_TOP - PAD_BOTTOM - ECHO_HEIGHT,
      );

      // Position update — direkt DOM, kein State (avoid 60Hz React-Renders).
      echo.style.top = `${PAD_TOP + trackH * percent}px`;

      // Phase-Übergänge
      if (percent < COVER_THRESHOLD) {
        if (colophonTimer) {
          window.clearTimeout(colophonTimer);
          colophonTimer = 0;
        }
        setPhase('cover');
      } else if (percent >= COLOPHON_THRESHOLD) {
        // D: erreicht 100% → 2s warten → fade-out (nur wenn nicht bereits gone)
        if (phaseRef.current !== 'gone' && !colophonTimer) {
          colophonTimer = window.setTimeout(() => {
            setPhase('gone');
            colophonTimer = 0;
          }, COLOPHON_FADE_DELAY_MS);
        }
      } else {
        // Zurück in die Story — Colophon-Timer abbrechen, "gone" verlassen.
        if (colophonTimer) {
          window.clearTimeout(colophonTimer);
          colophonTimer = 0;
        }
        // B: idle wenn ≥ IDLE_DELAY_MS keine Activity
        const elapsed = Date.now() - lastActivityRef.current;
        setPhase(elapsed >= IDLE_DELAY_MS ? 'idle' : 'active');
      }
    };
    raf = requestAnimationFrame(tick);

    // B: jede Activity setzt den Idle-Timer-Anker zurück
    const onActivity = () => {
      lastActivityRef.current = Date.now();
    };
    const activityTarget = scrollSource === 'window' ? window : el;
    activityTarget?.addEventListener('scroll', onActivity, { passive: true });
    activityTarget?.addEventListener('touchstart', onActivity, {
      passive: true,
    });
    activityTarget?.addEventListener('wheel', onActivity, { passive: true });
    activityTarget?.addEventListener('pointerdown', onActivity, {
      passive: true,
    });
    lastActivityRef.current = Date.now();

    return () => {
      cancelAnimationFrame(raf);
      if (colophonTimer) window.clearTimeout(colophonTimer);
      activityTarget?.removeEventListener('scroll', onActivity);
      activityTarget?.removeEventListener('touchstart', onActivity);
      activityTarget?.removeEventListener('wheel', onActivity);
      activityTarget?.removeEventListener('pointerdown', onActivity);
    };
  }, [scrollContainerRef, scrollSource]);

  const opacity = phase === 'cover' || phase === 'gone' ? 0 : 1;
  const isIdle = phase === 'idle';

  return (
    <>
      <style>{`
        @keyframes scrollyEchoIdle {
          0%, 100% { background: rgba(255, 255, 255, 0.30); }
          50%      { background: rgba(255, 255, 255, 0.55); }
        }
      `}</style>
      <div
        ref={echoRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 10,
          top: PAD_TOP,
          width: 6,
          height: ECHO_HEIGHT,
          borderRadius: 999,
          background: 'rgba(255, 255, 255, 0.35)',
          // Dark-Halo: macht Echo auf hellen Renderer-Inhalten sichtbar,
          // verschwindet auf dunklen optisch. Sehr dezent, nicht aufdringlich.
          boxShadow: '0 0 4px rgba(0, 0, 0, 0.5)',
          transition: 'top 50ms linear, opacity 400ms ease',
          pointerEvents: 'none',
          // Hoher z-index notwendig: scrolly-scroll-container Kinder haben
          // z-Werte bis 1000 (slide-container) und 998 (sticky-Element).
          // Trotz isolation:isolate auf scroll-container greift's hier nicht
          // zuverlässig — sicherheitshalber 9999.
          zIndex: 9999,
          opacity,
          animation: isIdle
            ? 'scrollyEchoIdle 2.4s ease-in-out infinite'
            : 'none',
        }}
      />
    </>
  );
}
